import { Component, JSX, h, Host, Prop, Element, Watch, ComponentInterface } from '@stencil/core';
import * as is from 'is_js';

@Component({
  tag: 'pan-buttons',
  styleUrl: './pan-buttons.scss'
})
export class PanButtonsComponent implements ComponentInterface {
  @Element() private hostElement: HTMLButtonElement;

  @Prop() public wrapperId: string;
  @Prop() public containerId: string;
  @Prop() public innerContainerId: string;
  @Prop() public itemIdPrefix: string;
  @Prop() public itemCount: number;
  @Prop() public focusCardIndex: number;

  private autoscrolling = false;
  private scrollbarWidth = 17;
  private wrapper: HTMLElement;
  private pointsBetweenCards: number[] = [0];

  public componentWillLoad(): void {
    window.addEventListener('resize', this.onResize, true);
    window.addEventListener('scroll', this.onScroll, true);
  }

  public componentDidRender(): void {
    if (this.itemCount) {
      this.checkForClosestCardToCenter();
      this.onResize();
      setTimeout(() => {
        this.moveCards(this.focusCardIndex ?? 0);
      });
    }
  }

  public disconnectedCallback(): void {
    window.removeEventListener('resize', this.onResize, true);
    window.removeEventListener('scroll', this.onScroll, true);
  }

  @Watch('itemCount')
  public watchHandler(): void {
    this.checkForClosestCardToCenter();
    this.onResize();
    setTimeout(() => {
      this.moveCards(this.focusCardIndex ?? 0);
    });
  }

  private onResize = (): void => {
    const elClientRect = document.getElementById(`${this.itemIdPrefix}0`);
    if (elClientRect) {
      const cardGutter = 30;
      const viewPortWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
      let firstCardTriggerPoint = 160; // offset for small cards
      if (viewPortWidth > 720) {
        firstCardTriggerPoint = 209; // offset for large cards
      }
      let pointsBetweenCardsTotal = firstCardTriggerPoint;
      this.pointsBetweenCards = [0, pointsBetweenCardsTotal];
      for (let x = 1; x < this.itemCount - 1; x++) {
        pointsBetweenCardsTotal += elClientRect.getBoundingClientRect().width + cardGutter / 2;
        this.pointsBetweenCards.push(pointsBetweenCardsTotal);
      }
      this.pointsBetweenCards.push(9999);
    }
    this.showPanButtons();
  };

  private showPanButtons(): void {
    const cardInnerContainer = document.getElementById(this.innerContainerId);
    this.wrapper = document.getElementById(this.wrapperId);
    if (this.wrapper && cardInnerContainer) {
      const spaWrapperClientRec = this.wrapper.getBoundingClientRect();
      const spaWrapperBottom = spaWrapperClientRec.top + spaWrapperClientRec.height;
      const cardInnerContainerRect = cardInnerContainer.getBoundingClientRect();

      if (cardInnerContainerRect.width > window.innerWidth - this.scrollbarWidth) {
        if (spaWrapperBottom < window.innerHeight) {
          this.hostElement.classList.add('hide-pan-buttons-container');
        } else {
          this.hostElement.classList.remove('hide-pan-buttons-container');
        }
      } else {
        this.hostElement.classList.add('hide-pan-buttons-container');
      }
    }
  }

  private moveCards(index: number): void {
    if (!this.autoscrolling) {
      if (!this.checkCardsCanFitViewPort()) {
        this.wrapper = document.getElementById(this.wrapperId);
        const cardXPos = this.getCenter(this.itemIdPrefix + index);
        if (this.wrapper) {
          this.wrapper.classList.remove('scroll-snap-type');
          let duration = 1000;
          if (is.ios()) {
            duration = 400;
          } else if (is.safari()) {
            duration = 200;
          }
          this.scrollToX(this.wrapper, cardXPos, duration, index);
        }
      }
    }
  }

  private checkCardsCanFitViewPort(): boolean {
    const elem = document.getElementById(this.innerContainerId);
    if (elem) {
      const box = elem.getBoundingClientRect();
      const minBreakpointToFitCards = box.width;
      let mql: MediaQueryList[] = [];
      mql = [window.matchMedia(`(min-width: ${minBreakpointToFitCards}px)`)];
      return mql[0].matches;
    }
    return false;
  }

  private selectPanButtons(index: number): void {
    if (document.getElementById(`panButton${Number(this.itemCount - 1).toString()}`)) {
      for (let x = 0; x < this.itemCount; x++) {
        if (x !== index) {
          document.getElementById(`panButton${x}`).classList.remove('panning-button-selected');
        } else {
          document.getElementById(`panButton${x}`).classList.add('panning-button-selected');
        }
      }
    }
  }

  private checkForClosestCardToCenter(): void {
    const wrapper = document.getElementById(this.wrapperId);
    if (wrapper) {
      const offsetLeft = wrapper.scrollLeft;

      for (let x = 0; x < this.pointsBetweenCards.length - 1; x++) {
        if (this.isBetween(offsetLeft, this.pointsBetweenCards[x], this.pointsBetweenCards[x + 1])) {
          this.selectPanButtons(x);
        }
      }
    }
    this.showPanButtons();
  }

  private onScroll = (): void => {
    this.checkForClosestCardToCenter();
  };

  private isBetween(value: number, lowerValue: number, upperValue: number): boolean {
    return (value - lowerValue) * (value - upperValue) <= 0;
  }
  private getCenter(obj: string): number {
    const viewPortWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const elem = document.getElementById(obj);
    this.wrapper = document.getElementById(this.wrapperId);
    if (elem) {
      const elClientRect = elem.getBoundingClientRect();
      const elemX = elClientRect.right - elClientRect.width / 2;
      const panel1ScrollLeft = this.wrapper.scrollLeft;
      const scrollToX = elemX - viewPortWidth / 2 + panel1ScrollLeft;
      return scrollToX;
    }
    return 0;
  }

  private easeInOutQuad(currentTime: number, startValue: number, changeInVal: number, duration: number): number {
    currentTime /= duration / 2;
    if (currentTime < 1) {
      return (changeInVal / 2) * currentTime * currentTime + startValue;
    }
    currentTime--;
    return (-changeInVal / 2) * (currentTime * (currentTime - 2) - 1) + startValue;
  }

  private scrollToX(container: HTMLElement, to: number, duration: number, index: number): void {
    const start = container.scrollLeft;
    const change = to - start;
    let currentTime = 0;
    const increment = 20;

    type AnimateScroll = () => void;

    const animateScroll: AnimateScroll = () => {
      this.autoscrolling = true;
      currentTime += increment;
      const val = this.easeInOutQuad(currentTime, start, change, duration);
      container.scrollLeft = val;

      if (currentTime < duration) {
        setTimeout(animateScroll, increment);
      } else {
        if (!is.ios() && !is.safari()) {
          this.wrapper.classList.add('scroll-snap-type');
        }
        this.autoscrolling = false;
        this.selectPanButtons(index);
      }
    };

    animateScroll();
  }

  private renderPanButtons(): JSX.Element[] {
    const buttons = [];
    for (let i = 0; i < this.itemCount; i++) {
      buttons.push(
        <div
          class="panning-button"
          id={`panButton${i}`}
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => {
            this.moveCards(i);
          }}
        >
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="50" fill="#001CB0" />
          </svg>
        </div>
      );
    }
    return buttons;
  }

  public render(): JSX.Element {
    return (
      <Host>
        <div class="pan-buttons-container" id="panButtons">
          {this.renderPanButtons()}
        </div>
      </Host>
    );
  }
}
